import { bareClient } from "./bareClient";

const SIGNING_KEY_STORE = "jose_signing_key";
const HKDF_INFO = "payload-signing-v1";

let signingKey: CryptoKey | null = null;
let keyExchangeInFlight: Promise<void> | null = null;

// ----------------------------------------------------------------------------
// Base64url helpers
// ----------------------------------------------------------------------------
function toB64url(buf: ArrayBuffer | Uint8Array): string {
  const arr = buf instanceof Uint8Array ? buf : new Uint8Array(buf);
  return btoa(String.fromCharCode(...arr))
    .replace(/\+/g, "-")
    .replace(/\//g, "_")
    .replace(/=/g, "");
}

function fromB64url(s: string): ArrayBuffer {
  const padded = s
    .replace(/-/g, "+")
    .replace(/_/g, "/")
    .padEnd(s.length + ((4 - (s.length % 4)) % 4), "=");
  return Uint8Array.from(atob(padded), (c) => c.charCodeAt(0)).buffer;
}

// ----------------------------------------------------------------------------
// Signing key lifecycle
// ----------------------------------------------------------------------------

/** Drop the in-memory + persisted signing key (e.g. on logout / 401). */
export function clearSigningKey(): void {
  signingKey = null;
  localStorage.removeItem(SIGNING_KEY_STORE);
}

/**
 * Restore a previously-derived signing key from localStorage on page load,
 * so the ECDH handshake doesn't need to repeat on every refresh.
 */
async function restoreSigningKey(): Promise<void> {
  const stored = localStorage.getItem(SIGNING_KEY_STORE);
  if (!stored) return;
  try {
    signingKey = await crypto.subtle.importKey(
      "raw",
      fromB64url(stored),
      { name: "HMAC", hash: { name: "SHA-256" }, length: 256 },
      false,
      ["sign"],
    );
  } catch {
    localStorage.removeItem(SIGNING_KEY_STORE);
  }
}

/** Resolves once the localStorage restore attempt above has finished. */
export const signingKeyRestored: Promise<void> = restoreSigningKey().catch(() => {});

async function runKeyExchange(tokenOverride?: string): Promise<void> {
  const token = tokenOverride ?? localStorage.getItem("token");
  if (!token) throw new Error("key-exchange: no token");

  const keyPair = await crypto.subtle.generateKey(
    { name: "ECDH", namedCurve: "P-256" },
    true,
    ["deriveBits"],
  );
  const publicKeyRaw = await crypto.subtle.exportKey("raw", keyPair.publicKey);

  const { data } = await bareClient.post(
    "/auth/key-exchange",
    { client_public_key: toB64url(publicKeyRaw) },
    { headers: { Authorization: `Bearer ${token}` } },
  );

  const serverPublicKey = await crypto.subtle.importKey(
    "raw",
    fromB64url(data.server_public_key as string),
    { name: "ECDH", namedCurve: "P-256" },
    false,
    [],
  );

  const sharedBits = await crypto.subtle.deriveBits(
    { name: "ECDH", public: serverPublicKey },
    keyPair.privateKey,
    256,
  );

  const hkdfBaseKey = await crypto.subtle.importKey(
    "raw",
    sharedBits,
    "HKDF",
    false,
    ["deriveKey"],
  );

  signingKey = await crypto.subtle.deriveKey(
    {
      name: "HKDF",
      hash: "SHA-256",
      salt: new Uint8Array(32),
      info: new TextEncoder().encode(HKDF_INFO),
    },
    hkdfBaseKey,
    { name: "HMAC", hash: { name: "SHA-256" }, length: 256 },
    true,
    ["sign"],
  );

  // Persist so the key survives page refreshes without re-running key exchange.
  const rawKey = await crypto.subtle.exportKey("raw", signingKey);
  localStorage.setItem(SIGNING_KEY_STORE, toB64url(rawKey));
}

/**
 * Kick off (or await an in-flight) ECDH handshake to establish the session
 * signing key. Safe to call concurrently — only one handshake runs at a time.
 *
 * Call this after login/MFA once the token is in localStorage, or let
 * `ensureSigningKey()` trigger it lazily on the first signed write. Pass
 * `tokenOverride` when the token isn't in localStorage yet (e.g. from inside
 * the login response handler, before the caller has stored it).
 */
export async function initKeyExchange(tokenOverride?: string): Promise<void> {
  if (signingKey) return;
  if (!keyExchangeInFlight) {
    keyExchangeInFlight = runKeyExchange(tokenOverride).finally(() => {
      keyExchangeInFlight = null;
    });
  }
  return keyExchangeInFlight;
}

/**
 * Returns the current signing key, restoring it from localStorage and/or
 * running the ECDH handshake first if necessary. Never throws — resolves to
 * `null` if the handshake couldn't complete (e.g. no token yet), and the
 * caller (the request interceptor) falls back to sending the request unsigned.
 */
export async function ensureSigningKey(): Promise<CryptoKey | null> {
  await signingKeyRestored;
  if (!signingKey) {
    await initKeyExchange().catch(() => {});
  }
  return signingKey;
}

// ----------------------------------------------------------------------------
// JWS signing
// ----------------------------------------------------------------------------

/**
 * Compact-serializes `body` as an HS256 JWS, using either a derived
 * `CryptoKey` (authenticated writes) or a raw nonce string (public writes).
 */
export async function signJWS(
  body: unknown,
  key: CryptoKey | string,
): Promise<string> {
  const enc = new TextEncoder();
  const header = toB64url(enc.encode(JSON.stringify({ alg: "HS256" })));
  const payload = toB64url(enc.encode(JSON.stringify(body)));
  const message = enc.encode(`${header}.${payload}`);

  const cryptoKey =
    key instanceof CryptoKey
      ? key
      : await crypto.subtle.importKey(
          "raw",
          enc.encode(key).buffer as ArrayBuffer,
          { name: "HMAC", hash: "SHA-256" },
          false,
          ["sign"],
        );

  const signature = new Uint8Array(
    await crypto.subtle.sign(
      { name: "HMAC" },
      cryptoKey,
      message.buffer as ArrayBuffer,
    ),
  );
  return `${header}.${payload}.${toB64url(signature)}`;
}